import React, { useState } from "react";
import * as StateLib from "../Utils/StateLib";
import { ConflictResolutionModal } from "@/appComponents/Blocks/Modals/ConflictResolutionModal/ConflictResolutionModal";
import { GetDoc, GetWSMgr } from "../Utils/StateLib";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";
import { ManageMetricsModal } from "@/appComponents/Blocks/Modals/ManageMetricsModal/ManageMetricsModal";
import { useTopic } from "@/hooks/useTopic";
import {
  PlanRecordEditorModal,
  basicEmployeeEmployeeFields
} from "@/appComponents/Blocks/Modals/RecordEditorModal/PlanRecordEditorModal";
import {
  AssignmentStatus,
  enumObjClassNames,
  enumObjectTypes,
  enumOverrideType,
  kEmployeeAssignmentStatusID,
  kEmployeePositionID,
  objTypeClassMap,
  enumBoxSubTypes,
  BOX_ACTIONS,
  enumUpdatePlanModes
} from "@coreapp/constants";
import { QChange } from "@coreapp/CoreApp/QChange";
import { QUpdatePlansRFC } from "@coreapp/CoreApp/QUpdatePlansRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { PlanManagerPositionEditorModal } from "@/appComponents/Blocks/Modals/RecordEditorModal/PlanManagerPositionEditorModal";
import { PlanManagerCreatePositionModal } from "@/appComponents/Blocks/Modals/RecordEditorModal/PlanManagerCreatePositionModal";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import {
  loadPositionById,
  loadEmployeeById
} from "@/appComponents/Blocks/Modals/RecordEditorModal/PositionEditorUtils";
import {
  createEmployeePositionClickableToast,
  createEntityUpdatedClickableToast,
  createMultipleEmployeesPositionClickableToast
} from "@coreapp/utils/clickableToastHandlers";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { appstr } from "@coreapp/utils/appstr";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { GetFlattenedChanges } from "@coreapp/utils/GetFlattenedChanges";
import { enumSidebarMode } from "@/appComponents/Navigation/RightSidebar/RightSidebar";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { ErrorBoundary } from "@/appComponents/Blocks/Utils/ErrorBoundary/ErrorBoundary";
import { GatedModals } from "@/appComponents/Blocks/Composites/ModalHost/GatedModals";
import { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { PlanSubmitModal } from "@/appComponents/Blocks/Modals/PlanSubmitModal/PlanSubmitModal";
import { PlanReviewModal } from "@/appComponents/Blocks/Modals/PlanReviewModal/PlanReviewModal";
import { PlanWithdrawModal } from "@/appComponents/Blocks/Modals/PlanWithdrawModal/PlanWithdrawModal";

/**
 * TODO: Ensure all consumers use this context for plan/submission info
 * TODO: Add TypeScript types for context value and provider
 */

const PlanManagerContext = React.createContext();

export const ReviewMode = {
  APPROVE: "approve",
  REJECT: "reject"
};

// Based on QChangeEditorPanel.prototype.GetChangesOnPanel
const AssertAddTypeFields = (changes, dbObj, overrideObj, planMgr) => {
  // Always inject the record ID (EE ID / PersonID) into every Add change regardless
  // of isNewOverride. Re-edits of existing overrides skip the full field injection
  // below, but omitting the record ID causes planHierarchyBuilder and
  // check_plan_chain to crash whenever this change becomes the only active one.
  const recordIDFieldDef = dbObj.GetFieldDefByID("PersonID");
  if (recordIDFieldDef) {
    const recordIDName = recordIDFieldDef.GetFieldName();
    if (changes[recordIDName] == null) {
      const recordIDValue = dbObj.GetFieldValue(recordIDFieldDef, false);
      if (recordIDValue != null) changes[recordIDName] = recordIDValue;
    }
  }

  const isNewOverride = !overrideObj.SmartGetFieldValue("override_id");
  if (!isNewOverride) {
    return changes;
  }

  const parentClass = dbObj.GetParentClass();

  const theDoc = planMgr.GetDoc();
  const fieldList = theDoc.GetFieldList2(false, false, false, false, false, true, parentClass);

  // read existing values
  for (const field in fieldList) {
    const fieldDef = fieldList[field];
    const fieldName = fieldDef.GetFieldName();

    const curValue = dbObj.GetFieldValue(fieldDef, false);
    if (changes[fieldName] == null && curValue != null) {
      changes[fieldName] = curValue;
    }
  }

  return changes;
};

const PlanManagerProvider = ({ children }) => {
  const { addToast } = useToast();
  const [planManager, setPlanManager] = useState({
    state: StateLib.GetWSMgr() && StateLib.GetWSMgr().GetPlanMgr()
  });
  const [isApprovalAlertDismissed, setIsApprovalAlertDismissed] = useState(false);
  const [isPlanStatusAlertDismissed, setIsPlanStatusAlertDismissed] = useState(false);

  const defaultEditRecord = {
    dataObj: null,
    override: null,
    type: null,
    branchAdd: false,
    initChanges: {},
    isMultiEdit: false,
    multiDataObjs: null,
    multiOverrides: null
  };

  const [conflictOverride, setConflictOverride] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [editRecord, setEditRecord] = useState({
    [enumObjectTypes.holder]: defaultEditRecord,
    [enumObjectTypes.position]: defaultEditRecord
  });
  const [showRecordEditor, setShowRecordEditor] = useState(false);
  const [showPositionEditor, setShowPositionEditor] = useState(false);
  const [showCreatePosition, setShowCreatePosition] = useState(false);
  const [parentPosition, setParentPosition] = useState(null);
  const [showManageMetrics, setShowManageMetrics] = useState(false);
  const [showClonePosition, setShowClonePosition] = useState(false);
  const [showAssignmentTab, setShowAssignmentTab] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [reviewModalState, setReviewModalState] = useState({ show: false, mode: null });
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const { handleSideBarModeChange, sideBarMode } = useRightSidebarContext();

  let editRecordRef = React.useRef(null);
  editRecordRef.current = editRecord;

  let showAssignmentsRef = React.useRef(null);
  showAssignmentsRef.current = showAssignmentTab;

  let sidebarModeRef = React.useRef(null);
  sidebarModeRef.current = sideBarMode;

  const onClosePositionEditor = () => {
    setEditRecord(prevState => ({
      ...prevState,
      [enumObjectTypes.position]: { ...defaultEditRecord }
    }));
    setShowPositionEditor(false);
    setShowCreatePosition(false);
    setShowClonePosition(false);
    setParentPosition(null);
  };

  const onCloseRecordEditor = () => {
    setEditRecord(prevState => ({
      ...prevState,
      [enumObjectTypes.holder]: { ...defaultEditRecord }
    }));
    setShowRecordEditor(false);
  };

  const onClosePositionAssignment = () => {
    setEditRecord(prevState => ({
      ...prevState,
      [enumObjectTypes.position]: { ...defaultEditRecord }
    }));
    setShowAssignmentTab(false);
    showAssignmentsRef.current = false;
  };

  const getEditTarget = override => {
    return editRecord[override.SmartGetFieldValue("objType")];
  };

  const handleClose = override => {
    if (override.SmartGetFieldValue("objType") === enumObjectTypes.position) {
      onClosePositionEditor();
    } else {
      onCloseRecordEditor();
    }
  };

  const SendUpdate = async (override, revertAll = false, onlyComment = false) => {
    const planMgr = planManager.state;
    let doTalentUpdate = false;
    if (!Array.isArray(override)) {
      const targetRec = getEditTarget(override);
      doTalentUpdate =
        targetRec.dataObj.fIsInPlan === true || targetRec.dataObj.fIsInPlan === false;
    }
    return await planMgr.SendUpdate(override, onlyComment, doTalentUpdate, revertAll);
  };

  const getPriorityChangeType = (...changeTypes) => {
    // The order of the changeTypePriority array determines the priority of the change types.
    // The first change type in the array will be the highest priority.
    const changeTypePriority = [
      enumOverrideType.delete,
      enumOverrideType.add,
      enumOverrideType.move,
      enumOverrideType.moveout,
      enumOverrideType.change,
      enumOverrideType.comment
    ];

    return changeTypes.reduce((highestChangeType, changeType) => {
      const highestChangeTypeValue = changeTypePriority.indexOf(highestChangeType);
      const changeTypeValue = changeTypePriority.indexOf(changeType);
      if (changeTypeValue < highestChangeTypeValue) {
        return changeType;
      }

      return highestChangeType;
    }, enumOverrideType.comment);
  };

  const onSaveMultiRecordEdit = async (data, targetRec, action = null) => {
    const { result, changeType } = data;
    const { multiOverrides } = targetRec;
    const planMgr = planManager.state;
    const wsMgr = GetWSMgr();
    const { comment: theComment, changes: curChanges, effectiveDate } = result;

    const newChangesExist = Object.keys(curChanges).length !== 0;

    if (!newChangesExist && !result.onlyComment) {
      handleClose(multiOverrides[0]);
      return;
    }

    const actionID = crypto.randomUUID();

    const updatedOverrides = multiOverrides.map((override, i) => {
      override.SmartSetFieldValue("Comment", theComment);

      // For reopen, don't let delete take priority - use the change type passed
      const finalChangeType =
        action === BOX_ACTIONS.REOPEN
          ? changeType
          : getPriorityChangeType(changeType, override.SmartGetFieldValue("Type"));

      override.SmartSetFieldValue("Type", finalChangeType);

      if (newChangesExist) {
        pushNewChange(
          override,
          changeType,
          curChanges,
          planMgr?.GetUseEffectiveDates() || false,
          effectiveDate,
          wsMgr,
          planMgr,
          actionID
        );
      }

      return override;
    });

    const response = await SendUpdate(updatedOverrides, false, false);
    const finalResult = response === false ? { success: false } : JSON.parse(response);
    const positionCount = multiOverrides.length;

    if (finalResult["success"]) {
      let template = "Successfully updated positions";
      let replacements = {
        count: positionCount.toString(),
        positions: (positionCount === 1 ? appstr("Position") : appstr("Positions")).toLowerCase()
      };

      if (action === BOX_ACTIONS.REOPEN) {
        template = "Entities are reopened";
        replacements = {
          __entities__: `${positionCount} ${(positionCount === 1 ? appstr("Position") : appstr("Positions")).toLocaleLowerCase()}`
        };
      }
      const boldSuccessMessage = createBoldToastMessage(template, replacements);

      addToast({
        ...boldSuccessMessage,
        status: TOAST_STATUS.SUCCESS
      });
    } else {
      addToast({
        message: appstr("Failed to update positions"),
        status: TOAST_STATUS.ERROR
      });
    }

    if (finalResult["success"]) {
      handleClose(multiOverrides[0]);
    }

    return finalResult;
  };

  const onSaveRecordEdit = async (data, action = null) => {
    const { result, override, changeType } = data;

    const targetRec = getEditTarget(override);
    if (targetRec?.isMultiEdit && targetRec?.multiOverrides) {
      return await onSaveMultiRecordEdit(data, targetRec, action);
    }

    const creatingFromTalentPool = override.SmartGetFieldValue("fromOutsidePlan");
    const assignmentChanges = result?.assignmentChanges;
    let planMgr = planManager.state;
    const wsMgr = GetWSMgr();
    let theComment = result.comment;
    let curChanges = result.changes;
    if (!creatingFromTalentPool && changeType === enumOverrideType.add) {
      curChanges = AssertAddTypeFields(curChanges, targetRec.dataObj, override, planMgr);
    }

    let effectiveDate = result.effectiveDate;
    override.SmartSetFieldValue("Comment", theComment);

    // For reopen, don't let delete take priority - use the change type passed
    const priorityChangeType =
      action === BOX_ACTIONS.REOPEN
        ? changeType
        : getPriorityChangeType(changeType, override.SmartGetFieldValue("Type"));

    override.SmartSetFieldValue("Type", priorityChangeType);

    const actionID = crypto.randomUUID();

    let newChangesExist;
    // While comments will not have keys in the changes list, newChangesExist will be true when the value of comment is changed.
    // The case of a new comment override w/ an empty TE value is handled in GetChangesOnPanel. In that case, 'comment' will NOT be added to the curChangeObj
    // However, blanking out the comment of an existing override WILL register as a newChange.
    if (changeType === enumOverrideType.comment && result["comment"] != null)
      newChangesExist = true;
    else newChangesExist = Object.keys(curChanges).length !== 0;

    let existingChange = override.lastActiveChange();
    let effectiveDatesEnabled = planMgr ? planMgr.GetUseEffectiveDates() : false;

    let dateChanged =
      existingChange &&
      effectiveDatesEnabled &&
      effectiveDate != null &&
      effectiveDate !== existingChange.GetChangeEffectiveDate();

    const assignmentChangesExist =
      assignmentChanges?.added?.length || assignmentChanges?.removed?.length;

    const overrideHasContent =
      creatingFromTalentPool ||
      newChangesExist ||
      result.madeFieldReverts ||
      result.onlyComment ||
      priorityChangeType === enumOverrideType.delete ||
      dateChanged ||
      result.revertAll;

    if (!overrideHasContent && !assignmentChangesExist) {
      // no changes on panel
      handleClose(override);
      return;
    }

    const isNewOverride = !override.SmartGetFieldValue("override_id");
    if (creatingFromTalentPool && isNewOverride) {
      const existingChange = GetFlattenedChanges(override);
      const dataObj = targetRec.dataObj;
      const parentClass = dataObj.GetParentClass();
      const parentFieldDef = parentClass.GetSupervisorIDFieldDef();
      const parentFieldName = parentFieldDef?.GetFieldName();
      let changes = {};
      if (parentFieldName && existingChange[parentFieldName]) {
        changes[parentFieldName] = existingChange[parentFieldName];
      }

      pushNewChange(
        override,
        changeType,
        AssertAddTypeFields(changes, dataObj, override, planMgr),
        effectiveDatesEnabled,
        effectiveDate,
        wsMgr,
        planMgr,
        actionID
      );
    }

    if (newChangesExist) {
      pushNewChange(
        override,
        creatingFromTalentPool ? enumOverrideType.change : changeType,
        curChanges,
        effectiveDatesEnabled,
        effectiveDate,
        wsMgr,
        planMgr,
        actionID
      );
    } else if (effectiveDatesEnabled && changeType !== enumOverrideType.comment) {
      var curChange = existingChange;
      if (existingChange == null && !creatingFromTalentPool) {
        curChange = new QChange();
        curChange.fWSMgr = wsMgr;
        curChange.fPlanMgr = planMgr;
        override.fChanges.push(curChange);
      }
      curChange.fEffectiveDate = effectiveDate;
    }
    if (result.branchAdd) override.fBranchAdd = true;

    let saveBlocked = false;
    let mainResponse;

    if (overrideHasContent) {
      mainResponse = await SendUpdate(override, result.revertAll, result.onlyComment);
      if (mainResponse === false) saveBlocked = true;
    }

    // Assignment changes should be handled after the main override(position object for position charts) is updated.
    // This ensures that conflict resolution for holders doesn't interact with stale position overrides.
    if (assignmentChanges) {
      const assignmentOverrides = handleAssigneeChanges(
        targetRec.dataObj,
        assignmentChanges,
        actionID
      );
      if (assignmentOverrides?.length > 0) {
        const response = await SendUpdate(assignmentOverrides, false, false);
        handleUpdateResponse(response, override, assignmentChanges);
        if (response === false) saveBlocked = true;
      }
    }

    if (overrideHasContent && !(mainResponse !== false && saveBlocked)) {
      handleUpdateResponse(mainResponse, override, null, action);
    }

    handleClose(override);

    return saveBlocked ? { success: false } : undefined;
  };

  const handleUpdateResponse = (response, override, assignmentChanges = null, action = null) => {
    const finalResult = JSON.parse(response);
    const targetRec = getEditTarget(override);
    if (finalResult["success"]) {
      if (assignmentChanges) {
        const { added, removed } = assignmentChanges;
        const positionLabel = FormatPositionLabel(targetRec.dataObj);
        const positionId = targetRec.dataObj.SmartGetFieldValue("PositionID");

        if (added?.length > 1) {
          addToast({
            content: createMultipleEmployeesPositionClickableToast(
              "{count} {employees} are assigned to {position}",
              added.length,
              positionLabel,
              positionId
            ),
            status: TOAST_STATUS.SUCCESS,
            timeoutMS: 10000
          });
        } else if (added?.length === 1) {
          const employeeName = sanitizeBlockedValue(added[0].SmartGetFieldValue("Name"));
          const employeeId = added[0].SmartGetFieldValue("EmployeeID");
          addToast({
            content: createEmployeePositionClickableToast(
              "Employee {employee} assigned to {position}",
              employeeName,
              positionLabel,
              positionId,
              employeeId
            ),
            status: TOAST_STATUS.SUCCESS,
            timeoutMS: 10000
          });
        }

        if (removed?.length > 1) {
          addToast({
            content: createMultipleEmployeesPositionClickableToast(
              "{count} {employees} are unassigned from {position}",
              removed.length,
              positionLabel,
              positionId
            ),
            status: TOAST_STATUS.SUCCESS,
            timeoutMS: 10000
          });
        } else if (removed?.length === 1) {
          const employeeName = sanitizeBlockedValue(removed[0].SmartGetFieldValue("Name"));
          addToast({
            content: createEmployeePositionClickableToast(
              "{employee} is unassigned from {position}",
              employeeName,
              positionLabel,
              positionId
            ),
            status: TOAST_STATUS.SUCCESS,
            timeoutMS: 10000
          });
        }
      } else {
        const flatChanges = GetFlattenedChanges(override);
        const dataObj = targetRec.dataObj;
        const empPosFieldName = dataObj
          .GetParentClass?.()
          ?.GetFieldDefByID?.(kEmployeePositionID)
          ?.GetFieldName();
        const newPositionId = empPosFieldName ? (flatChanges[empPosFieldName] ?? null) : null;
        let content = createEntityUpdatedClickableToast(dataObj, {}, newPositionId);
        if (action === BOX_ACTIONS.REOPEN) {
          content = createEntityUpdatedClickableToast(
            dataObj,
            { template: "The entity is reopened" },
            newPositionId
          );
        }
        addToast({
          content,
          status: TOAST_STATUS.SUCCESS,
          duration: 10000
        });
      }
    } else {
      addToast({
        message: appstr("Unable to Save Changes"),
        status: TOAST_STATUS.ERROR
      });
    }
  };

  const pushNewChange = (
    override,
    changeType,
    changes,
    effectiveDatesEnabled,
    effectiveDate,
    wsMgr,
    planMgr,
    actionID = null
  ) => {
    let newChange = new QChange();
    newChange.fParentOverride = override;
    newChange.fWSMgr = wsMgr;
    newChange.fPlanMgr = planMgr;
    newChange.fDelta = changes;
    newChange.fChangeType = changeType;
    newChange.fChangeAuthor = wsMgr.GetUserName();
    if (effectiveDatesEnabled) newChange.fEffectiveDate = effectiveDate;
    if (actionID) newChange.SetActionID(actionID);

    override.fChanges.push(newChange);
  };

  const reassignHolder = (holder, newPositionId = "", actionID = null) => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    const overrideClass = planMgr?.fOverrideClass;
    if (!wsMgr || !planMgr || !overrideClass) {
      return;
    }

    const objDb = overrideClass.GetObjDB();
    const boxClass = objDb?.GetClassByName(enumObjClassNames.box);
    if (!boxClass) {
      return;
    }

    const empPosIdFieldDef = boxClass.GetFieldDefByID("Employee PositionID");
    const empPosFieldName = empPosIdFieldDef?.GetFieldName();
    if (!empPosFieldName) {
      // Bad state - Required field not found.
      return;
    }

    let holderChangeType = enumOverrideType.move;
    let holderChangeDict = {
      author: wsMgr.GetUserName(),
      date: new Date().toUTCString(),
      data: {
        [empPosFieldName]: newPositionId,
        [kEmployeeAssignmentStatusID]: AssignmentStatus.Assigned,
        Orphan: 0
      }
    };

    if (newPositionId === "") {
      holderChangeDict.data.Orphan = 1;
      holderChangeDict.data[kEmployeeAssignmentStatusID] = AssignmentStatus.Unassigned;
    }

    const holderOverride = planMgr.GetActiveOverrideOrCreate(holder);
    holderOverride.SmartSetFieldValue("Type", holderChangeType);

    const change = new QChange();
    change.InitChange(wsMgr, holderChangeDict, holderOverride, holder, overrideClass);
    change.UpdateChange(holderChangeDict, holderChangeType);
    if (actionID) change.SetActionID(actionID);
    holderOverride.fChanges?.push(change);

    return holderOverride;
  };

  const handleAssigneeChanges = (positionObj, assignments, actionID = null) => {
    const { added, removed } = assignments;
    const assignmentOverrides = [];

    added?.forEach(employee => {
      const newPositionId = positionObj.SmartGetFieldValue("Position ID");
      const nextHolderAssignment = reassignHolder(employee, newPositionId, actionID);
      if (nextHolderAssignment) {
        assignmentOverrides.push(nextHolderAssignment);
      }
    });

    removed?.forEach(employee => {
      const nextHolderUnAssignment = reassignHolder(employee, "", actionID);
      if (nextHolderUnAssignment) {
        assignmentOverrides.push(nextHolderUnAssignment);
      }
    });

    return assignmentOverrides;
  };

  const retrieveOverrideDBOBj = override => {
    return new Promise(resolve => {
      const recordID = override.SmartGetFieldValue("RefID");
      const displayObj = override.SmartGetFieldValue("displayObject");
      const doc = GetDoc();
      const objDB = doc.GetObjDB();
      const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
      const className = objTypeClassMap[objType];
      const objClass = objDB.GetClassByName(className);
      const personIDDef = objClass.GetRecordIDFieldDef();
      let dbObj = objClass.FindObject(personIDDef, recordID, true);

      if (dbObj) {
        if (!dbObj.fCloneBase && displayObj) dbObj.fCloneBase = displayObj.Clone(true);
        resolve(dbObj);
        return;
      }

      if (objType === enumObjectTypes.position) {
        loadPositionById(recordID, loadedPosition => {
          if (loadedPosition) {
            const dbObj = loadedPosition.Clone();
            dbObj.fCloneBase = loadedPosition.Clone(true);
            override.ApplyChanges(dbObj);
            resolve(dbObj);
          } else {
            const dbObj = displayObj?.Clone();
            if (dbObj && displayObj) dbObj.fCloneBase = displayObj.Clone(true);
            resolve(dbObj);
          }
        });
      } else {
        loadEmployeeById(recordID, loadedEmployee => {
          if (loadedEmployee) {
            const dbObj = loadedEmployee.Clone();
            dbObj.fCloneBase = loadedEmployee.Clone(true);
            override.ApplyChanges(dbObj);
            resolve(dbObj);
          } else {
            const dbObj = displayObj?.Clone();
            if (dbObj && displayObj) dbObj.fCloneBase = displayObj.Clone(true);
            resolve(dbObj);
          }
        });
      }
    });
  };

  const recordEditorContextValue = {
    theData: editRecord,
    onCloseRecordEditor,
    onClosePositionEditor,
    onSaveRecordEdit,
    retrieveOverrideDBOBj,
    showRecordEditor,
    showPositionEditor,
    showCreatePosition,
    showClonePosition,
    parentPosition
  };

  const assignmentContextValue = {
    theData: editRecord,
    onClosePositionAssignment,
    showAssignmentTab,
    setShowAssignmentTab,
    onSaveRecordEdit,
    parentPosition
  };

  const onSaveMetrics = metrics => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr.fPlanManager;
    var savePlansRFC = new QUpdatePlansRFC();
    var accountName = wsMgr.GetAccountName();
    savePlansRFC.InitUpdatePlans(wsMgr);
    var user = wsMgr.GetUserName();
    let curPlanObj = planManager.state.GetPlanInfo();
    curPlanObj = JSON.parse(JSON.stringify(curPlanObj));
    curPlanObj["metrics"] = metrics;
    savePlansRFC.SetParams(
      accountName,
      user,
      enumUpdatePlanModes.saveplan,
      JSON.stringify(curPlanObj)
    );
    Globals._rfcMgr.QueueRFC(savePlansRFC);
    setShowManageMetrics(false);
    planMgr.SimulateSaveFeedback();
  };

  const onCloseManageMetrics = () => {
    setShowManageMetrics(false);
  };

  const manageMetricsContextValue = {
    onSaveMetrics,
    onCloseManageMetrics,
    setShowManageMetrics,
    showManageMetrics
  };

  const onSavePlanUsers = data => {
    const { users, emailInvite, defaultRole } = data;
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr.fPlanManager;
    let curPlanObj = planMgr.GetPlanInfo();
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    const savePlansRFC = new QUpdatePlansRFC();
    savePlansRFC.InitUpdatePlans(wsMgr);
    curPlanObj["users"] = users;
    curPlanObj["emailInvite"] = emailInvite;
    if (defaultRole) {
      curPlanObj["defaultRole"] = defaultRole;
    }
    var requestData = {
      planID: curPlanObj["ID"],
      users: users,
      emailInvite: emailInvite,
      defaultRole: defaultRole
    };
    savePlansRFC.SetParams(
      accountName,
      user,
      enumUpdatePlanModes.updateplanusers,
      JSON.stringify(requestData)
    );
    Globals._rfcMgr.QueueRFC(savePlansRFC);
    planMgr.UpdateContext();
    planMgr.SimulateSaveFeedback();
  };

  const manageUsersContextValue = {
    onSavePlanUsers
  };

  const openSubmitModal = () => {
    setShowSubmitModal(true);
  };

  const closeSubmitModal = () => {
    setShowSubmitModal(false);
  };

  const planSubmitContextValue = {
    showSubmitModal,
    openSubmitModal,
    closeSubmitModal
  };

  const openReviewModal = mode => {
    setReviewModalState({ show: true, mode });
  };

  const closeReviewModal = () => {
    setReviewModalState({ show: false, mode: null });
  };

  const planReviewContextValue = {
    showReviewModal: reviewModalState.show,
    reviewMode: reviewModalState.mode,
    openReviewModal,
    closeReviewModal
  };

  const openWithdrawModal = () => {
    setShowWithdrawModal(true);
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
  };

  const planWithdrawContextValue = {
    showWithdrawModal,
    openWithdrawModal,
    closeWithdrawModal
  };

  /**
   * @param {*} override
   * @param {QObjDBObject | undefined} dbObj
   * @param {*} changeType
   * @param {*} branchAdd
   * @param {*} initChanges
   * @param {*} isMismatch
   * @param {import("@coreapp/constants").TBoxActions} action
   */
  const handleRecordEdit = async (
    override,
    dbObj,
    changeType,
    branchAdd = false,
    initChanges = {},
    isMismatch = false,
    action = null
  ) => {
    const handled = await handleRecordEditorEvent(
      override,
      dbObj,
      changeType,
      branchAdd,
      initChanges,
      isMismatch,
      action
    );

    if (!handled) return false;

    const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
    if (objType === enumObjectTypes.position) {
      setShowPositionEditor(true);
    } else {
      setShowRecordEditor(true);
    }
  };

  const handlePositionAssignment = async (
    override,
    dbObj,
    changeType,
    branchAdd = false,
    initChanges = {},
    isMismatch = false
  ) => {
    const handled = await handleRecordEditorEvent(
      override,
      dbObj,
      changeType,
      branchAdd,
      initChanges,
      isMismatch
    );
    if (!handled) return;

    setShowAssignmentTab(true);
    if (sideBarMode !== enumSidebarMode.employeeList.value) {
      handleSideBarModeChange(enumSidebarMode.employeeList.value);
    }
  };

  const handleRecordEditorEvent = async (
    override,
    dbObj,
    changeType,
    branchAdd = false,
    initChanges = {},
    isMismatch = false,
    action = null
  ) => {
    const displayObj = override.SmartGetFieldValue("displayObject");
    if (override && !changeType) changeType = override.SmartGetFieldValue("Type");

    if (!dbObj) {
      dbObj = await retrieveOverrideDBOBj(override);
    }

    const isAux = dbObj.SmartGetFieldValue("isAux");
    if (isAux != null && isAux !== "" && isAux !== 0) {
      const idFieldDef = dbObj.GetRecordIDFieldDef();
      const auxFieldDef = dbObj.GetFieldDefByID("isAux");

      const overrideClass = override.GetParentClass();
      const overrideIDFieldDef = overrideClass.GetFieldDefByID("override_id");
      const refIdFieldDef = overrideClass.GetFieldDefByID("RefID");

      const auxOverrideId = override.GetFieldValue(overrideIDFieldDef);
      const refId = override.GetFieldValue(refIdFieldDef);
      const baseRefId = dbObj.GetAuxCleanID(idFieldDef, auxFieldDef);

      const suffix = refId.replace(baseRefId, "");

      const baseOverrideId = auxOverrideId.replace(suffix, "");
      const baseOverride = overrideClass.FindObject(overrideIDFieldDef, baseOverrideId);

      override = baseOverride;
      dbObj = await retrieveOverrideDBOBj(baseOverride);
    }

    if (displayObj && !dbObj.fCloneBase) dbObj.fCloneBase = displayObj.Clone(true);

    //if (planManager?.state?.OverrideHasConflicts(override)) {
    //  handleRecordConflictEvent(override);
    //  return false;
    // }

    const nextObj = {
      dataObj: dbObj,
      override: override,
      type: changeType,
      branchAdd,
      initChanges,
      isMismatch,
      action
    };

    const key = override.SmartGetFieldValue("objType");
    setEditRecord(prevState => ({ ...prevState, [key]: nextObj }));

    return true;
  };

  const handleRecordConflictEvent = overrideObj => {
    setConflictOverride(overrideObj);
    setShowConflictModal(true);
  };

  const handleCreatePosition = parentPositionObj => {
    // Store the parent position object to use in the CreatePositionModal
    setParentPosition(parentPositionObj);
    // Show the CreatePositionModal
    setShowCreatePosition(true);
  };

  const handleClonePosition = positionObj => {
    // Store the position object to use in the CreatePositionModal
    setParentPosition(positionObj);
    setShowClonePosition(true);
  };

  const handleMultiRecord = async (
    overrides,
    dbObjs,
    changeType,
    initChanges = {},
    action = null
  ) => {
    if (!overrides?.length || !dbObjs?.length) return;

    const objType = overrides[0].SmartGetFieldValue("objType") ?? enumObjectTypes.position;

    const multiEditObj = {
      dataObj: dbObjs[0],
      override: overrides[0],
      type: changeType,
      branchAdd: false,
      initChanges,
      isMultiEdit: true,
      multiDataObjs: dbObjs,
      multiOverrides: overrides,
      action
    };

    setEditRecord(prevState => ({ ...prevState, [objType]: multiEditObj }));

    if (objType === enumObjectTypes.position) {
      setShowPositionEditor(true);
    } else {
      setShowRecordEditor(true);
    }
  };

  const conflictContextValue = {
    showConflictModal,
    setShowConflictModal,
    conflictOverride,
    setConflictOverride
  };

  React.useEffect(() => {
    SubscribeExternalModuleReactAPI(
      {
        stateSetter: setPlanManager
      },
      "planmgr"
    );
  }, []);

  React.useEffect(() => {
    if (planManager && planManager["state"] != null) {
      planManager["state"].handleRecord = handleRecordEdit;
      planManager["state"].handleConflict = handleRecordConflictEvent;
      planManager["state"].handleCreatePosition = handleCreatePosition;
      planManager["state"].handleClonePosition = handleClonePosition;
      planManager["state"].handleAssignments = handlePositionAssignment;
      planManager["state"].handleMultiRecord = handleMultiRecord;
    }
  }, [planManager, handleRecordEdit, handleRecordConflictEvent, handleMultiRecord]);

  // Reset alert dismissed states when plan changes or reopens
  React.useEffect(() => {
    if (planManager?.state) {
      setIsApprovalAlertDismissed(false);
      setIsPlanStatusAlertDismissed(false);
    }
  }, [
    planManager?.state?.GetPlanInfo?.()?.ID,
    planManager?.state?.GetPlanSubmission?.()?.submissionID,
    planManager?.state?.GetPlanSubmission?.()?.status
  ]);

  // Handle holder assign area box selection
  const handleHolderAssignBoxSelect = (theElement, selectIt) => {
    if (selectIt === false) return false;

    const wsMgr = GetWSMgr();
    const planMgr = wsMgr.GetPlanMgr();
    const doc = wsMgr.GetDoc();

    // not compatible in non position / non plan contexts (for now)
    if (!planMgr || !doc.IsPositionDocument()) {
      return false;
    }

    const boxSubType = theElement.GetBoxSubType();
    let parentBox = null;

    // we only handle the selection reassignment for holder boxes
    if (boxSubType !== enumBoxSubTypes.holderAreaBox) {
      return false;
    }

    const pageNode = theElement["nodeRef"];
    const containerNode = pageNode?.GetContainerNode();
    parentBox = containerNode.GetChartBox();

    if (parentBox.IsSelected()) return false; // position already selected, no need to continue

    if (sideBarMode !== enumSidebarMode.employeeList.value) {
      handleSideBarModeChange(enumSidebarMode.employeeList.value);
    }

    // Select the parent position box
    const cp = wsMgr?.fCanvasPanel;
    cp?.DoSelectElement(parentBox, selectIt);
    return true;
  };

  // Handle canvas selection changes for position assignment
  useTopic("CanvasSelectionChange", ({ theElement, selectIt }) => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr.GetPlanMgr();

    if (!theElement || !planMgr || !wsMgr) {
      return;
    }

    // Handle chart box selection (handleChartSelection logic from EmployeeList)
    if (theElement instanceof QChartBox) {
      const selectedDbObj = theElement?.GetDataObj();
      if (
        sidebarModeRef?.current === enumSidebarMode.employeeList.value &&
        selectedDbObj?.InferObjectType() === enumObjectTypes.position
      ) {
        if (selectIt) {
          // Only call HandlePositionAssignments if we're not already in assignment mode for this position
          if (!showAssignmentsRef.current) {
            planMgr.HandlePositionAssignments(selectedDbObj);
          }
        } else {
          // position deselected - close assignment tab
          onClosePositionAssignment();
        }
      } else if (selectIt === true) {
        // Holder box clicked - redirect to parent position
        const handled = handleHolderAssignBoxSelect(theElement, selectIt);
        if (handled) {
          // Clear the placeholder selection state
          const cp = wsMgr.fCanvasPanel;
          cp?.DoSelectElement(theElement, false);
        }
      }
    }
  });

  const dismissApprovalAlert = () => {
    setIsApprovalAlertDismissed(true);
  };

  const dismissPlanStatusAlert = () => {
    setIsPlanStatusAlertDismissed(true);
  };

  return (
    <PlanManagerContext.Provider
      value={{
        planManager,
        setPlanManager,
        recordEditorContextValue,
        manageMetricsContextValue,
        manageUsersContextValue,
        conflictContextValue,
        assignmentContextValue,
        planSubmitContextValue,
        planReviewContextValue,
        planWithdrawContextValue,
        dismissApprovalAlert,
        dismissPlanStatusAlert,
        isApprovalAlertDismissed,
        isPlanStatusAlertDismissed
      }}
    >
      <GatedModals>
        <ErrorBoundary>
          <PlanRecordEditorModal />
          <PlanManagerPositionEditorModal />
          <PlanManagerCreatePositionModal />
          <ManageMetricsModal />
          <ConflictResolutionModal />
          <PlanSubmitModal />
          <PlanReviewModal />
          <PlanWithdrawModal />
        </ErrorBoundary>
      </GatedModals>
      {children}
    </PlanManagerContext.Provider>
  );
};

export const usePlanManagerContext = () => React.useContext(PlanManagerContext);

export default PlanManagerProvider;
